import Head from "next/head";
import { type NextPageContext } from "next";
import { getCache } from "@fwa/src/store/SwrCache";

import { pageFetcher } from "@fwa/src/services/apiClient";
import { fwsUrSuperTeamPageSlug } from "@fwa/src/services/superTeamService";
import { queryAsString } from "@fwa/src/utils/urlUtils";
import { isBrowser } from "@fwa/src/utils/browserUtils";
import { getShareContentFromEntity } from "@fwa/src/utils/shareUtils";

import { SuperTeamPage } from "@fwa/src/components/SuperTeamPage";
import { ErrorPageWrapper } from "@fwa/src/components/ErrorPageWrapper";

import { type SuperTeamPageType, type ResponseError } from "@fwa/src/types";

type PageProps = {
  pageData?: SuperTeamPageType;
  error?: ResponseError;
  themeName?: string;
};

const Page = ({ pageData, themeName, error }: PageProps) => {
  const { title, description, image } = pageData
    ? getShareContentFromEntity(pageData)
    : { title: "", description: "", image: "" };
  return (
    <ErrorPageWrapper error={error}>
      {pageData && (
        <>
          <Head>
            <title>{title}</title>
            {/* sharing meta */}
            <meta property="og:title" content={title} />
            {description?.length ? (
              <>
                <meta name="description" content={description} />
                <meta property="og:description" content={description} />
              </>
            ) : null}
            <meta property="og:url" content={pageData?.url} />
            <meta property="og:type" content="website" />
            {image ? (
              <>
                <meta name="twitter:card" content="summary_large_image" />
                <meta property="og:image" content={image} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
              </>
            ) : null}
          </Head>
          <SuperTeamPage pageData={pageData} themeName={themeName} />
        </>
      )}
    </ErrorPageWrapper>
  );
};

Page.getInitialProps = async ({
  query,
  res,
}: NextPageContext): Promise<PageProps> => {
  if (res) {
    res.setHeader(
      "Cache-Control",
      "max-age=3600, stale-while-revalidate=86400",
    );
  }

  const cache = getCache();
  const { slug } = query;
  const slugString = queryAsString(slug);
  const url = fwsUrSuperTeamPageSlug({ slug: slugString || "" });
  const getThemeName = (page: SuperTeamPageType) =>
    page?.theme || page?.fundraisingPageType?.brand?.name;

  if (isBrowser) {
    const cachedData = cache.get(url)?.data as SuperTeamPageType;
    if (cachedData)
      return {
        pageData: cachedData,
        error: undefined,
        themeName: getThemeName(cachedData),
      };
  }

  const { pageData, error } = await pageFetcher<SuperTeamPageType>(url);

  return {
    pageData,
    error,
    themeName: getThemeName(pageData!),
  };
};

export default Page;
